import React, { useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik, type FormikProps } from "formik";
import * as Yup from "yup";

import { useUpdateTicketSizeForDealOpportunity } from "@vega-api/generated/coinvest-deal-opportunity-actions/coinvest-deal-opportunity-actions";
import { useUpdateTicketSizeForDealOpportunityGp } from "@vega-api/generated/coinvest-deal-opportunity-gp-actions/coinvest-deal-opportunity-gp-actions";
import { getGetDealTrackingForGpQueryKey } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import { getGetDealOpportunityLimitedPartnerViewV3QueryKey } from "@vega-api/generated/deal-opportunity-lp-view/deal-opportunity-lp-view";
import { CheckboxControlType } from "@vega-core/components/Form/Controls/Checkbox/CheckboxControl";
import { CheckboxField } from "@vega-core/components/Form/Fields/CheckboxField";
import { MoneyField } from "@vega-core/components/Form/Fields/MoneyField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { type CoreCurrency, type CoreMoney } from "@vega-core/models/money";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

type SubmitTicketSizeFormValues = {
  minimum: { amount: string; currency: CoreCurrency };
  maximum: { amount: string; currency: CoreCurrency };
  acknowledgement: boolean;
};

export type SubmitTicketSizeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  dealOpportunityId: string;
  initialValues?: { minimum: CoreMoney; maximum: CoreMoney };
  dealCurrency: CoreCurrency;
  requiresAcknowledgement?: boolean;
  isGpContext?: boolean;
  dealId?: string;
  maxAmount?: CoreMoney;
};

export const SubmitTicketSizeModal = ({
  isOpen,
  onClose,
  dealOpportunityId,
  initialValues: initialValuesFromProps,
  dealCurrency,
  requiresAcknowledgement = true,
  isGpContext = false,
  dealId,
  maxAmount,
}: SubmitTicketSizeModalProps) => {
  let minAmountSchema = Yup.number()
    .required(m.coInvestments_submitTicketSize_modal_minInputRequired())
    .moreThan(
      0,
      m.coInvestments_submitTicketSize_modal_minInputMustBeGreaterThanZero(),
    );

  let maxAmountSchema = Yup.number().required(
    m.coInvestments_submitTicketSize_modal_maxInputRequired(),
  );

  if (maxAmount) {
    const exceedsTargetMessage =
      m.coInvestments_submitTicketSize_modal_amountExceedsTarget();
    minAmountSchema = minAmountSchema.max(
      maxAmount.amount,
      exceedsTargetMessage,
    );
    maxAmountSchema = maxAmountSchema.max(
      maxAmount.amount,
      exceedsTargetMessage,
    );
  }

  const validationSchema = Yup.object({
    minimum: Yup.object({ amount: minAmountSchema }),
    maximum: Yup.object({ amount: maxAmountSchema }),
    acknowledgement: Yup.boolean().oneOf(
      [true],
      m.coInvestments_submitTicketSize_modal_acknowledgmentRequired(),
    ),
  }).test({
    name: "maximum-at-least-minimum",
    test: (values, context) => {
      const { minimum, maximum } = values;

      if (minimum.amount === undefined || maximum.amount === undefined) {
        return true;
      }

      if (maximum.amount < minimum.amount) {
        return context.createError({
          path: "maximum.amount",
          message:
            m.coInvestments_submitTicketSize_modal_maxInputMustBeAtLeastMin(),
        });
      }

      return true;
    },
  });

  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  // LP hook
  const { mutate: updateTicketSizeLp, isPending: isSubmittingLp } =
    useUpdateTicketSizeForDealOpportunity({
      mutation: {
        onSuccess: () => {
          showToast({
            title: m.status_success(),
            description: m.coInvestments_submitTicketSize_toast_success(),
            variant: "success",
          });
          queryClient.invalidateQueries({
            queryKey:
              getGetDealOpportunityLimitedPartnerViewV3QueryKey(
                dealOpportunityId,
              ),
          });
          onClose();
        },
        onError: (error) => {
          showErrorToast(error, {
            defaultMessage: m.coInvestments_submitTicketSize_toast_error(),
          });
        },
      },
    });

  // GP hook
  const { mutate: updateTicketSizeGp, isPending: isSubmittingGp } =
    useUpdateTicketSizeForDealOpportunityGp({
      mutation: {
        onSuccess: () => {
          showToast({
            title: m.status_success(),
            description: m.coInvestments_submitTicketSize_toast_success(),
            variant: "success",
          });

          if (dealId) {
            queryClient.invalidateQueries({
              queryKey: getGetDealTrackingForGpQueryKey(dealId),
            });
          }
          onClose();
        },
        onError: (error) => {
          showErrorToast(error, {
            defaultMessage: m.coInvestments_submitTicketSize_toast_error(),
          });
        },
      },
    });
  const updateTicketSize = isGpContext
    ? updateTicketSizeGp
    : updateTicketSizeLp;
  const isSubmitting = isGpContext ? isSubmittingGp : isSubmittingLp;
  const formRef = useRef<FormikProps<SubmitTicketSizeFormValues>>(null);

  const initialValues: SubmitTicketSizeFormValues = {
    minimum: {
      amount: initialValuesFromProps
        ? initialValuesFromProps.minimum.amount.toString()
        : "",
      currency: dealCurrency,
    },
    maximum: {
      amount: initialValuesFromProps
        ? initialValuesFromProps.maximum.amount.toString()
        : "",
      currency: dealCurrency,
    },
    acknowledgement: !requiresAcknowledgement,
  };

  const handleSubmit = (values: SubmitTicketSizeFormValues) => {
    updateTicketSize({
      dealOpportunityId,
      data: {
        minimum: {
          amount: parseFloat(values.minimum.amount),
          currency: values.minimum.currency,
        },
        maximum: {
          amount: parseFloat(values.maximum.amount),
          currency: values.maximum.currency,
        },
      },
    });
  };

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainActionLabel: m.btn_submit(),
        mainAction: () => formRef.current?.submitForm(),
        mainActionText: m.btn_submit(),
        mainActionVariant: "primary",
        disabled: isSubmitting,
        secondaryAction: onClose,
        secondaryActionLabel: m.btn_cancel(),
        secondaryActionText: m.btn_cancel(),
        disabledSecondary: isSubmitting,
      }}
      dataTestId="submit-ticket-size-modal"
      fluidHeight
      open={isOpen}
      setOpen={onClose}
      title={m.coInvestments_submitTicketSize_modal_title()}
    >
      <Formik
        initialValues={initialValues}
        innerRef={formRef}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        <Form>
          <div className="flex flex-col gap-4 mb-2 max-w-lg">
            <div className="flex gap-2">
              <MoneyField
                allowAbbreviations
                className="flex-1 min-w-0"
                currency={dealCurrency}
                isRequired
                label={m.coInvestments_submitTicketSize_modal_minInputLabel()}
                name="minimum"
              />
              <span
                aria-hidden="true"
                className="body-small-medium text-text/secondary mt-10"
              >
                &mdash;
              </span>
              <MoneyField
                allowAbbreviations
                className="flex-1 min-w-0"
                currency={dealCurrency}
                isRequired
                label={m.coInvestments_submitTicketSize_modal_maxInputLabel()}
                name="maximum"
              />
            </div>
            {requiresAcknowledgement ? (
              <CheckboxField
                className="text-start"
                label={m.coInvestments_submitTicketSize_modal_acknowledgment()}
                name="acknowledgement"
                type={CheckboxControlType.ACKNOWLEDGE}
              />
            ) : null}
          </div>
        </Form>
      </Formik>
    </ActionaryModal>
  );
};
